package com.hyperether.goodjob.repository

import androidx.compose.ui.graphics.painter.BitmapPainter
import com.hyperether.goodjob.managers.FileManager
import com.hyperether.goodjob.models.AddressObject
import com.hyperether.goodjob.models.CompletionReportStatus
import com.hyperether.goodjob.models.Contact
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.JobStatus
import com.hyperether.goodjob.models.Plan
import com.hyperether.goodjob.models.Repeat
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.models.Site
import com.hyperether.goodjob.models.Skills
import com.hyperether.goodjob.models.Step
import com.hyperether.goodjob.models.SuperAdmin
import com.hyperether.goodjob.models.Team
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.models.UserStatus
import com.hyperether.goodjob.models.Workspace
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.repository.remote.GoodJobApiImpl
import com.hyperether.goodjob.repository.remote.auth.AppleLoginRequest
import com.hyperether.goodjob.repository.remote.auth.GoogleLoginRequest
import com.hyperether.goodjob.repository.remote.auth.MicrosoftLoginRequest
import com.hyperether.goodjob.repository.remote.model.AllJobReportsRequest
import com.hyperether.goodjob.repository.remote.model.AllUsersByRoleRequest
import com.hyperether.goodjob.repository.remote.model.CompletionReportRequest
import com.hyperether.goodjob.repository.remote.model.CompletionReportResponse
import com.hyperether.goodjob.repository.remote.model.ConfirmationResponse
import com.hyperether.goodjob.repository.remote.model.EphemeralKeyRequest
import com.hyperether.goodjob.repository.remote.model.EphemeralKeyResponse
import com.hyperether.goodjob.repository.remote.model.FileResult
import com.hyperether.goodjob.repository.remote.model.FinishJobRequest
import com.hyperether.goodjob.repository.remote.model.FinishJobResponse
import com.hyperether.goodjob.repository.remote.model.ForgotPasswordRequest
import com.hyperether.goodjob.repository.remote.model.ForgotPasswordResponse
import com.hyperether.goodjob.repository.remote.model.JobRequest
import com.hyperether.goodjob.repository.remote.model.JobStepsUpdateRequest
import com.hyperether.goodjob.repository.remote.model.LoginResponse
import com.hyperether.goodjob.repository.remote.model.MessageResponse
import com.hyperether.goodjob.repository.remote.model.ParsedAddress
import com.hyperether.goodjob.repository.remote.model.ResetPasswordRequest
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.repository.remote.model.SetPasswordRequest
import com.hyperether.goodjob.repository.remote.model.SetPasswordResponse
import com.hyperether.goodjob.repository.remote.model.SetupPaymentIntentResponse
import com.hyperether.goodjob.repository.remote.model.SiteRequest
import com.hyperether.goodjob.repository.remote.model.SkillRequest
import com.hyperether.goodjob.repository.remote.model.SubscribeToPlanResponse
import com.hyperether.goodjob.repository.remote.model.TeamRequest
import com.hyperether.goodjob.repository.remote.model.TeamsByWorkspaceRequest
import com.hyperether.goodjob.repository.remote.model.UploadFileResponse
import com.hyperether.goodjob.repository.remote.model.UserRequest
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.file_deleted
import com.hyperether.goodjob.resources.no_data_received
import com.hyperether.goodjob.resources.unexpected_error
import com.hyperether.goodjob.resources.unknown_error
import com.hyperether.goodjob.resources.workspace_not_found
import com.hyperether.goodjob.util.ContactUtil
import com.hyperether.goodjob.util.FileUtil
import com.hyperether.goodjob.util.LocationUtil
import com.hyperether.resources.stringResourcePlain
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.flow
import org.jetbrains.compose.resources.getString
import org.lighthousegames.logging.logging

class WebRepositoryImpl(
    private val prefs: PrefsManager,
    private val goodJobApiImpl: GoodJobApiImpl,
    private val fileManager: FileManager
) : Repository {
    val log = logging("WebRepositoryImpl")

    override suspend fun findPlace(name: String): List<ParsedAddress>? {
        return goodJobApiImpl.findPlace(name)?.map { LocationUtil.parseFormattedAddress(it) }
    }

    // *******************************  WORKSPACE *******************************

    override suspend fun deleteAllWorkspaces() {
        // TODO not needed, we delete it on logout in app (keep it on BE)
    }

    override suspend fun insertWorkspace(workspace: Workspace) {

    }

    override suspend fun getWorkspacesFlow(): Flow<List<Workspace>> {
        return flow {
            if (prefs.getUser()?.role.equals(Role.superAdmin.name, ignoreCase = true)) {
                val resource = getAllWorkspaces()
                if (resource is Resource.Success) {
                    val response = resource.data
                    if (response != null) {
                        log.d("getWorkspacesFlow") { "data: $response" }
                        emit(resource.data.distinct())
                    } else {
                        emit(emptyList())
                        log.d("getWorkspacesFlow") { "response null" }
                    }
                } else {
                    emit(emptyList())
                }
            } else {
                val resource = getUserWorkspaces()
                if (resource is Resource.Success) {
                    val response = resource.data
                    if (response != null) {
                        log.d("getWorkspacesFlow") { "data: $response" }
                        emit(resource.data.distinct())
                    } else {
                        emit(emptyList())
                        log.d("getWorkspacesFlow") { "response null" }
                    }
                } else {
                    emit(emptyList())
                }
            }
        }.catch { exception ->
            log.d("getWorkspacesFlow") { "exception: $exception" }
            emit(emptyList())
        }
    }

    override suspend fun getWorkspaceById(id: String): Workspace? {
        var workspace: Workspace? = null
        getWorkspacesFlow().collect { workspaceList ->
            workspace = workspaceList.find { it._id == id }
        }
        return workspace
    }

    override suspend fun addWorkspace(name: String, workspaceType: String): Resource<Workspace> {
        val response = goodJobApiImpl.addWorkspace(name, workspaceType, getToken())
        addDefaultContact(response.data)
        return response
    }

    override suspend fun deleteWorkspaceById(id: Long) {
        // TODO
    }

    override suspend fun addDefaultContact(workspace: Workspace?) {
        workspace?.let {
            val user = getUser()
            val contact = Contact(
                name = workspace.name,
                contactType = "",
                industry = "",
                addressObject = AddressObject(
                    address = user?.address,
                    city = user?.city,
                    country = user?.country,
                    zip = user?.zip
                ),
                email = user?.email,
                phone = user?.phone,
                personName = user?.fullName,
                personEmail = user?.email,
                personPhone = user?.phone,
                workspaceId = workspace._id
            )
            addContact(contact)
        }
    }

    override suspend fun getUserWorkspaces(): Resource<List<Workspace>> {
        return try {
            when (val apiResponse = goodJobApiImpl.getAllWorkspacesId(getToken())) {
                is Resource.Success -> {
                    val workspaceResponse = apiResponse.data
                    workspaceResponse?.forEach { workspace ->
                        try {
                            val remoteId = workspace._id
                            if (remoteId.isNullOrBlank()) {
                                log.d("Workspace Sync Error") { "Skipping workspace with null or blank ID: ${workspace.name}" }
                                return@forEach
                            }
                        } catch (e: Exception) {
                            log.d("Workspace Sync Error") {
                                "Failed to sync workspace: ${workspace._id}, error: ${e.message}"
                            }
                        }
                    }
                    Resource.Success(workspaceResponse)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getAllWorkspaces(): Resource<List<Workspace>> {
        return try {
            when (val apiResponse = goodJobApiImpl.getAllWorkspaces(getToken())) {
                is Resource.Success -> {
                    val workspaceResponse = apiResponse.data?.toMutableList()
                    workspaceResponse?.forEach { workspace ->
                        try {
                            val remoteId = workspace._id
                            if (remoteId.isNullOrBlank()) {
                                log.d("Workspace Sync Error") { "Skipping workspace with null or blank ID: ${workspace.name}" }
                                return@forEach
                            }
                        } catch (e: Exception) {
                            log.d("Workspace Sync Error") {
                                "Failed to sync workspace: ${workspace._id}, error: ${e.message}"
                            }
                        }
                    }
                    Resource.Success(workspaceResponse)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateWorkspaceLastAccessedById(
        lastAccessed: String,
        workspaceId: String
    ): Resource<Workspace> {
        return try {
            val workspace = getWorkspaceById(workspaceId)
            workspace?.let {
                workspace.lastAccessed = lastAccessed
                val request = Mapper().toUpdateWorkspaceRequest(workspace)
                val apiResponse = goodJobApiImpl.updateWorkspace(request, workspaceId, getToken())
                when (apiResponse) {
                    is Resource.Success -> {
                        val workspaceResponse = apiResponse.data
                        Resource.Success(workspaceResponse)
                    }

                    is Resource.Error -> {
                        log.d("updateWorkspaceLastAccessedById") {
                            "Failed to update workspace: $workspaceId, error: ${apiResponse.text}"
                        }
                        Resource.Error(apiResponse.text)
                    }
                }
            } ?: run {
                log.d("updateWorkspaceLastAccessedById") { "Workspace with ID $workspaceId not found" }
                Resource.Error(stringResourcePlain(Res.string.workspace_not_found) + " $workspaceId")
            }
        } catch (e: Exception) {
            log.d("updateWorkspaceLastAccessedById") {
                "An unexpected error occurred: ${e.message}"
            }
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    // *******************************  PLAN *******************************

    override suspend fun getAllPlans(): Resource<List<Plan>> {
        return goodJobApiImpl.getAllPlans(getToken())
    }

    override suspend fun getPlansFlow(): Flow<List<Plan>> {
        return flow {
            val resource = goodJobApiImpl.getAllPlans(getToken())
            if (resource is Resource.Success && resource.data != null) {
                emit(resource.data)
            } else {
                emit(emptyList())
            }
        }.catch { exception ->
            emit(emptyList())
        }
    }

    override suspend fun getPlan(id: String): Plan? {
        return when (val result = goodJobApiImpl.getPlanById(getToken(), id)) {
            is Resource.Success -> {
                log.d("getPlanById") { "Success fetching plan: ${result.data}" }
                result.data
            }

            is Resource.Error -> {
                log.d("getPlanById") { "Error fetching plan: ${result.text}" }
                null
            }
        }
    }

    override suspend fun deletePlanById(id: String) {
        // TODO
    }

    // *******************************  USER *******************************

    override suspend fun getUsersFlow(): Flow<List<User>> {
        return flow {
            val resource = getUsers()
            if (resource is Resource.Success) {
                val response = resource.data
                if (response != null) {
                    log.d("getUsersFlow") { "data: $response" }
                    emit(resource.data.distinct())
                } else {
                    emit(emptyList())
                    log.d("getUsersFlow") { "response null" }
                }
            } else {
                emit(emptyList())
            }
        }.catch { exception ->
            log.d("getUsersFlow") { "exception: $exception" }
            emit(emptyList())
        }
    }

    override suspend fun getUsers(): Resource<List<User>> {
        return try {
            val apiResponse = goodJobApiImpl.getAllUsersByRole(
                getToken(), AllUsersByRoleRequest(prefs.getWorkspaceId())
            )

            when (apiResponse) {
                is Resource.Success -> {
                    val response = apiResponse.data

                    if (response?.isNotEmpty() == true) {
                        val currentUser = getUser()
                        response.forEach { user ->
                            try {
                                val remoteId = user._id
                                if (remoteId.isNullOrBlank()) {
                                    log.d("Users Sync Error") { "Skipping contact with null or blank ID: ${user.getName()}" }
                                    return@forEach
                                }
                                if (remoteId == currentUser?._id) {
                                    // Update logged user in shared prefs
                                    saveUser(user)
                                }
                            } catch (e: Exception) {
                                log.d("Users Sync Error") {
                                    "Failed to sync users: ${user._id}, error: ${e.message}"
                                }
                            }
                        }
                    }

                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun confirmation(hash: String): Resource<ConfirmationResponse> {
        val result = goodJobApiImpl.confirmation(hash)
        if (result is Resource.Success) {
            result.data?.let {
                saveToken(it.token ?: "")
                saveRefreshToken(it.refreshToken ?: "")
                saveUser(it.data)
            }
        }
        return result
    }

    override suspend fun resendConfirmationEmail(email: String): Resource<String> {
        return goodJobApiImpl.resendConfirmationEmail(email)
    }

    override suspend fun addUser(userRequest: UserRequest): Resource<User> {
        return try {
            when (val apiResponse = goodJobApiImpl.addUser(getToken(), userRequest)) {
                is Resource.Success -> {
                    val user = apiResponse.data
                    log.d("addUser") { "addUser SUCCESS $user" }
                    checkRestrictions()
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    log.d("addUser") { "addUser ERROR ${apiResponse.text}" }
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.d("addUser") { "addUser EXCEPTION ${e.message}" }
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateUser(
        userId: String,
        userRequest: UserRequest
    ): Resource<User> {
        val id = if (userId.contains("profileSettings")) {
            userId.substringAfter("-")
        } else {
            userId
        }
        return try {
            when (val apiResponse =
                goodJobApiImpl.updateUserById(getToken(), id, userRequest)) {
                is Resource.Success -> {
                    val user = apiResponse.data
                    if (userId.contains("profileSettings")) {
                        if (user != null) {
                            saveUser(user)
                            getUsers()
                        }
                    }
                    log.d("updateUser") { "updateUser SUCCESS $user" }
                    checkRestrictions()
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    log.d("updateUser") { "updateUser ERROR ${apiResponse.text}" }
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.d("updateUser") { "updateUser EXCEPTION ${e.message}" }
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteUser(id: String): Resource<String> {
        val result = goodJobApiImpl.deleteUser(getToken(), id)
        checkRestrictions()
        return result
    }

    override suspend fun getUserById(id: String): Resource<User> {
        return try {
            when (val apiResponse = goodJobApiImpl.getUserById(getToken(), id)) {
                is Resource.Success -> {
                    val user = apiResponse.data
                    log.d("getUserById") { "getUserById SUCCESS $user" }
                    Resource.Success(user)
                }

                is Resource.Error -> {
                    log.d("getUserById") { "getUserById ERROR ${apiResponse.text}" }
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.d("getUserById") { "getUserById EXCEPTION ${e.message}" }
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override fun saveUser(user: User) {
        prefs.saveUser(user)
    }

    override fun getUser(): User? {
        return prefs.getUser()
    }

    override suspend fun logoutUser() {
        log.d { "logoutUser" }
        prefs.saveEmail("")
        prefs.saveToken("")
        prefs.saveRefreshToken("")
        prefs.saveUser(null)
        prefs.saveWorkspaceId("")
        prefs.removeAll()
    }

    // ***************** CONTACT *****************

    override suspend fun getContactsFlow(): Flow<List<Contact>> {
        return flow {
            val resource = goodJobApiImpl.getAllContactsByWorkspace(getWorkspaceId(), getToken())
            if (resource is Resource.Success) {
                val response = resource.data
                if (response != null) {
                    IndustryManager.setIndustriesFromContacts(response)
                    log.d("getContactsFlow") { "data: $response" }
                    emit(resource.data.distinct())
                } else {
                    emit(emptyList())
                    log.d("getContactsFlow") { "response null" }
                }
            } else {
                emit(emptyList())
            }
        }.catch { exception ->
            log.d("getContactsFlow") { "exception: $exception" }
            emit(emptyList())
        }
    }

    override suspend fun getContact(id: String): Contact? {
        return when (val result = goodJobApiImpl.getContactById(id, getToken())) {
            is Resource.Success -> {
                result.data
            }

            is Resource.Error -> {
                null
            }
        }
    }

    override suspend fun addContact(contact: Contact): Resource<Contact> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.addContact(getToken(), Mapper().toContactRequest(contact))) {
                is Resource.Success -> {
                    apiResponse.data?.let {
                        addDefaultSite(it)
                    }

                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateContact(
        contact: Contact
    ): Resource<Contact> {
        return try {
            val contactId =
                contact.id ?: throw IllegalArgumentException("Contact ID cannot be null")
            val siteId = getContact(contactId)?.let { contactDb ->
                getSitesByContactId(contactId)
                    .find { ContactUtil.isSiteCreatedFromContact(it, contactDb) }?.id
            }
            when (val apiResponse = goodJobApiImpl.updateContact(
                contactId,
                getToken(),
                Mapper().toContactRequest(contact)
            )) {
                is Resource.Success -> {
                    val contactResponse = apiResponse.data
                    siteId?.let { it1 ->
                        contactResponse?.let {
                            updateSite(
                                it1,
                                contactResponse.getDefaultSite()
                            )
                        }
                    }
                    Resource.Success(contactResponse)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteContactById(id: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteContact(getToken(), id)) {
                is Resource.Success -> {
                    // This should be done by BE
//                    val sites = getSitesByContactId(id)
//                    sites.forEach {
//                        it.id?.let { siteId ->
//                            deleteSiteById(siteId)
//                        }
//                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getAllContactsByWorkspace(): Resource<List<Contact>> {
        return try {
            if (getWorkspaceId().isNotEmpty()) {
                val apiResponse = goodJobApiImpl.getAllContactsByWorkspace(
                    getWorkspaceId(), getToken()
                )

                when (apiResponse) {
                    is Resource.Success -> {
                        val contactResponse = apiResponse.data
                        IndustryManager.setIndustriesFromContacts(contactResponse)
                        Resource.Success(contactResponse)
                    }

                    is Resource.Error -> {
                        Resource.Error(apiResponse.text)
                    }
                }
            } else {
                Resource.Error(stringResourcePlain(Res.string.unknown_error))
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    suspend fun addDefaultSite(contact: Contact) {
        addSite(contact.getDefaultSite())
    }

    override suspend fun addSite(site: Site): Resource<Site> {
        return try {
            when (val apiResponse = goodJobApiImpl.addSite(getToken(), site)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteSiteById(id: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteSiteById(getToken(), id)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateSite(siteId: String, site: Site): Resource<Site> {
        return try {
            when (val apiResponse = goodJobApiImpl.updateSite(getToken(), siteId, site)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getSitesFlow(): Flow<List<Site>> = flow {
        val result = getAllContactsByWorkspace()
        if (result is Resource.Success) {
            val contacts = result.data.orEmpty()
            val sites = mutableListOf<Site>()

            contacts.forEach { contact ->
                contact.id?.let {
                    when (val siteResult = getSitesForContactRemote(it)) {
                        is Resource.Success -> sites.addAll(siteResult.data.orEmpty())
                        is Resource.Error -> emit(emptyList())
                    }
                }
            }
            log.d("getSitesFlow") { "data: $sites" }
            emit(sites)
        } else {
            log.d("getSitesFlow") { "error: empty" }
            emit(emptyList())
        }
    }

    override suspend fun getSiteById(id: String): Site? {
        return when (val result = goodJobApiImpl.getSiteById(siteId = id, token = getToken())) {
            is Resource.Success -> {
                log.d("getSiteById") { "data: ${result.data}" }
                result.data
            }

            is Resource.Error -> {
                log.d("getSiteById") { "error: ${result.text}" }
                null
            }
        }
    }

    override suspend fun getSitesByContactId(contactId: String): List<Site> {
        return when (val siteResult = getSitesForContactRemote(contactId)) {
            is Resource.Success -> siteResult.data.orEmpty()
            is Resource.Error -> emptyList()
        }
    }

    override suspend fun getSitesByContactIdFlow(contactId: String): Flow<List<Site>> {
        TODO("Not yet implemented")
    }

    override suspend fun getSitesForContactRemote(contactId: String): Resource<List<Site>> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.getSitesForContact(getToken(), SiteRequest(contactId))) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    // ********************* JOB *********************

    override suspend fun createJob(jobRequest: JobRequest): Resource<Job> {
        return try {
            when (val apiResponse = goodJobApiImpl.createJob(getToken(), jobRequest)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        log.d("createJob") { "Create job SUCCESS ${response.jobTitle}" }
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    log.d("createJob") { "Create job ERROR ${apiResponse.text}" }
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.d("createJob") { "Create job  EXCEPTION ${e.message}" }
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateJob(
        jobId: String,
        jobRequest: JobRequest
    ): Resource<Job> {
        return try {
            jobRequest.apply {
                repeatInterval =
                    repeatInterval.takeIf { repeat != Repeat.never.name } ?: emptyList()
                dueDate = dueDate.takeIf { repeat != Repeat.never.name } ?: ""
            }
            when (val apiResponse = goodJobApiImpl.updateJob(getToken(), jobId, jobRequest)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getJobById(jobId: String): Resource<Job> {
        return try {
            when (val apiResponse = goodJobApiImpl.getJobById(token = getToken(), jobId = jobId)) {
                is Resource.Success -> {
                    val job = apiResponse.data
                    when (val reportResult = getJobReportByJobId(jobId)) {
                        is Resource.Success -> {
                            job?.completionReport = Mapper().objectAsJson(reportResult.data)
                            Resource.Success(job)
                        }

                        is Resource.Error -> {
                            Resource.Success(job)
                        }
                    }
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getAllJobsForWorkspace(): Resource<List<Job>> {
        return try {
            if (getWorkspaceId().isNotEmpty()) {
                val apiResponse = goodJobApiImpl.getAllJobsForSpecificWorkspace(
                    getToken(),
                    getWorkspaceId()
                )

                when (apiResponse) {
                    is Resource.Success -> {
                        Resource.Success(apiResponse.data)
                    }

                    is Resource.Error -> {
                        Resource.Error(apiResponse.text)
                    }
                }
            } else {
                Resource.Error(stringResourcePlain(Res.string.unknown_error))
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getAllJobsForUser(): Resource<List<Job>> {
        return try {
            when (val apiResponse = goodJobApiImpl.getAllJobsForSpecificUser(getToken())) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response?.isNotEmpty() == true) {
                        getAllJobReports()
                    }
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getAllJobsForSpecificContact(contactParentId: String): Resource<List<Job>> {
        return try {
            when (val apiResponse = goodJobApiImpl.getAllJobsForSpecificContact(
                token = getToken(),
                contactParentId = contactParentId
            )) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getJobsFlow(): Flow<List<Job>> {
        return flow {
            if (getToken().isBlank())
                emit(emptyList())
            val isEmployee = getUser()?.role.equals(Role.employee.name, ignoreCase = true)
            val resource = if (isEmployee) {
                getAllJobsForUser()
            } else {
                getAllJobsForWorkspace()
            }
            if (resource is Resource.Success) {
                val response = resource.data
                if (response != null) {
                    log.d("getJobsFlow") { "data: $response" }
                    emit(resource.data.distinct())
                } else {
                    emit(emptyList())
                    log.d("getJobsFlow") { "response null" }
                }
            } else {
                emit(emptyList())
            }
        }.catch { exception ->
            log.d("getJobsFlow") { "exception: $exception" }
            emit(emptyList())
        }
    }

    override suspend fun getJobByIdFlow(jobId: String): Flow<Job?> {
        return flow {
            val resource = getJobById(jobId)
            if (resource is Resource.Success) {
                val response = resource.data
                if (response != null) {
                    log.d("getJobByIdFlow") { "data: $response" }
                    emit(resource.data)
                } else {
                    emit(null)
                    log.d("getJobByIdFlow") { "response null" }
                }
            } else {
                emit(null)
            }
        }.catch { exception ->
            log.d("getJobByIdFlow") { "exception: $exception" }
            emit(null)
        }
    }

    override suspend fun getJobsByContactIdFlow(id: String): Flow<List<Job>> {
        return flow {
            val resource = getAllJobsForSpecificContact(id)
            if (resource is Resource.Success) {
                val response = resource.data
                if (response != null) {
                    log.d("getJobsByContactIdFlow") { "data: $response" }
                    emit(resource.data)
                } else {
                    emit(emptyList())
                    log.d("getJobsByContactIdFlow") { "response null" }
                }
            } else {
                emit(emptyList())
            }
        }.catch { exception ->
            log.d("getJobsByContactIdFlow") { "exception: $exception" }
            emit(emptyList())
        }
    }

    override suspend fun getAllJobReports(): Resource<List<CompletionReportResponse>> {
        return try {
            val apiResponse =
                goodJobApiImpl.getAllJobReports(getToken(), AllJobReportsRequest(getWorkspaceId()))

            when (apiResponse) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteJobById(id: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteJobById(getToken(), id)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun submitCompletionReport(request: CompletionReportRequest): Resource<CompletionReportResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.submitCompletionReport(getToken(), request)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        when (response.status) {
                            CompletionReportStatus.pendingApproval.name,
                            CompletionReportStatus.rejected.name -> {
                                updateJobStatus(response.jobId, JobStatus.pendingApproval)
                            }

                            CompletionReportStatus.pending.name -> {

                            }

                            CompletionReportStatus.completed.name -> {
                                updateJobStatus(response.jobId, JobStatus.completed)
                            }
                        }
                    }
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getJobReportByJobId(jobId: String): Resource<CompletionReportResponse> {
        return try {
            val apiResponse = goodJobApiImpl.getJobReportByJobId(token = getToken(), jobId = jobId)
            when (apiResponse) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateJobStatus(jobId: String, jobStatus: JobStatus) {
        val resource = getJobById(jobId)
        if (resource is Resource.Success) {
            resource.data?.let {
                it.status = jobStatus.name
                updateJob(jobId, Mapper().toJobUpdateRequest(it))
            }
        }
    }

    override suspend fun finishJob(finishJobRequest: FinishJobRequest): Resource<FinishJobResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.finishJob(getToken(), finishJobRequest)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateJobStepsStatus(
        jobId: String,
        steps: List<Step>
    ): Resource<Job> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.updateJobStepsStatus(
                    getToken(),
                    JobStepsUpdateRequest(jobId, steps)
                )) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    //***************** SKILLS *********************************

    override suspend fun addSkill(skill: Skills): Resource<Skills> {
        return try {
            when (val apiResponse = goodJobApiImpl.addSkill(
                getToken(),
                getWorkspaceId(),
                SkillRequest(name = skill.name, workspaceId = getWorkspaceId())
            )) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getSkillsFlow(): Flow<List<Skills>> {
        return flow {
            val resource = getSkillsByWorkspace()
            if (resource is Resource.Success) {
                val response = resource.data
                if (response != null) {
                    log.d("getSkillsFlow") { "data: $response" }
                    emit(resource.data.distinct())
                } else {
                    emit(emptyList())
                    log.d("getSkillsFlow") { "response null" }
                }
            } else {
                emit(emptyList())
            }
        }.catch { exception ->
            log.d("getSkillsFlow") { "exception: $exception" }
            emit(emptyList())
        }
    }

    override suspend fun getSkillsByWorkspace(): Resource<List<Skills>> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.getSkillsByWorkspace(getToken(), getWorkspaceId())) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteAllSkills() {
        // TODO not needed, we delete it on logout in app (keep it on BE)
    }

    //***************** TEAMS *********************************

    override suspend fun getTeamsFlow(): Flow<List<Team>> {
        return flow {
            val resource = getTeams()
            if (resource is Resource.Success) {
                val response = resource.data
                if (response != null) {
                    log.d("getSkillsFlow") { "data: $response" }
                    emit(resource.data.distinct())
                } else {
                    emit(emptyList())
                    log.d("getSkillsFlow") { "response null" }
                }
            } else {
                emit(emptyList())
            }
        }.catch { exception ->
            log.d("getSkillsFlow") { "exception: $exception" }
            emit(emptyList())
        }
    }

    override suspend fun createTeam(name: String): Resource<Team> {
        return try {
            when (val apiResponse = goodJobApiImpl.createTeam(
                getToken(),
                teamRequest = TeamRequest(
                    name = name,
                    workspaceId = getWorkspaceId(),
                    employees = emptyList()
                )
            )) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getTeams(): Resource<List<Team>> {
        return try {
            if (getWorkspaceId().isNotEmpty()) {
                val apiResponse = goodJobApiImpl.getAllTeamsByWorkspace(
                    getToken(),
                    TeamsByWorkspaceRequest(getWorkspaceId())
                )

                when (apiResponse) {
                    is Resource.Success -> {
                        val response = apiResponse.data
                        Resource.Success(response)
                    }

                    is Resource.Error -> {
                        Resource.Error(apiResponse.text)
                    }
                }
            } else {
                Resource.Error(stringResourcePlain(Res.string.unknown_error))
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteTeamById(id: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteTeam(getToken(), id)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    //***************** LOGIN *********************************

    override suspend fun register(
        name: String,
        email: String,
        password: String
    ): Resource<User> {
        return try {
            when (val apiResponse = goodJobApiImpl.register(name, email, password)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("register") { "Register SUCCESS ${data.email}" }
                        saveUser(data)
                        Resource.Success(data)
                    } else {
                        log.d("register") { "Register ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.d("register") { "Register An error occurred" }
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.d("register") { "Register Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun login(email: String, password: String): Resource<LoginResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.login(email, password)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("login") { "Login SUCCESS ${data.user.email}" }
                        saveToken(data.token)
                        saveRefreshToken(data.refreshToken)
                        saveUser(data.user)
                        Resource.Success(data)
                    } else {
                        log.d("login") { "Login ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "Login ERROR An error occurred" }
                    val errorMessage = apiResponse.text ?: "Failed to login: ${apiResponse.text}"
                    val errorInternalCode = apiResponse.errorInternalCode
                    Resource.Error(errorMessage, errorInternalCode)
                }
            }
        } catch (e: Exception) {
            log.e { "Login Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun loginGoogle(request: GoogleLoginRequest): Resource<LoginResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.loginGoogle(request)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("login") { "Login SUCCESS ${data.user.email}" }
                        saveToken(data.token)
                        saveRefreshToken(data.refreshToken)
                        saveUser(data.user)
                        Resource.Success(data)
                    } else {
                        log.d("login") { "Login ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "Login ERROR An error occurred" }
                    val errorMessage = apiResponse.text ?: "Failed to login: ${apiResponse.text}"
                    val errorInternalCode = apiResponse.errorInternalCode
                    Resource.Error(errorMessage, errorInternalCode)
                }
            }
        } catch (e: Exception) {
            log.e { "Login Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun loginApple(request: AppleLoginRequest): Resource<LoginResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.loginApple(request)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("login") { "Login SUCCESS ${data.user.email}" }
                        saveToken(data.token)
                        saveRefreshToken(data.refreshToken)
                        saveUser(data.user)
                        Resource.Success(data)
                    } else {
                        log.d("login") { "Login ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "Login ERROR An error occurred" }
                    val errorMessage = apiResponse.text ?: "Failed to login: ${apiResponse.text}"
                    val errorInternalCode = apiResponse.errorInternalCode
                    Resource.Error(errorMessage, errorInternalCode)
                }
            }
        } catch (e: Exception) {
            log.e { "Login Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun loginMicrosoft(request: MicrosoftLoginRequest): Resource<LoginResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.loginMicrosoft(request)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("login") { "Login SUCCESS ${data.user.email}" }
                        saveToken(data.token)
                        saveRefreshToken(data.refreshToken)
                        saveUser(data.user)
                        Resource.Success(data)
                    } else {
                        log.d("login") { "Login ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "Login ERROR An error occurred" }
                    val errorMessage = apiResponse.text ?: "Failed to login: ${apiResponse.text}"
                    val errorInternalCode = apiResponse.errorInternalCode
                    Resource.Error(errorMessage, errorInternalCode)
                }
            }
        } catch (e: Exception) {
            log.e { "Login Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun setWorkspace(
        workspaceId: String
    ): Resource<SuperAdmin> {
        return goodJobApiImpl.setWorkspace(workspaceId, getToken())
    }

    override suspend fun forgotPassword(forgotPasswordRequest: ForgotPasswordRequest): Resource<ForgotPasswordResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.forgotPassword(forgotPasswordRequest)) {
                is Resource.Success -> {
                    val data = apiResponse.data

                    if (data != null) {
                        log.d("forgotPassword") { "SUCCESS $data" }
                        Resource.Success(data)
                    } else {
                        log.d("forgotPassword") { "ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "forgotPassword Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun resetPassword(resetPasswordRequest: ResetPasswordRequest): Resource<ForgotPasswordResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.resetPassword(resetPasswordRequest)) {
                is Resource.Success -> {
                    val data = apiResponse.data

                    if (data != null) {
                        log.d("resetPassword") { "SUCCESS $data" }
                        Resource.Success(data)
                    } else {
                        log.d("resetPassword") { "ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "resetPassword Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun setPassword(setPasswordRequest: SetPasswordRequest): Resource<SetPasswordResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.setPassword(setPasswordRequest)) {
                is Resource.Success -> {
                    val data = apiResponse.data

                    if (data != null) {
                        log.d("setPassword") { "SUCCESS $data" }
                        Resource.Success(data)
                    } else {
                        log.d("setPassword") { "ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "setPassword Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun getEphemeralKey(ephemeralKeyRequest: EphemeralKeyRequest): Resource<EphemeralKeyResponse> {
        return goodJobApiImpl.getEphemeralKey(
            getToken(),
            EphemeralKeyRequest(getUser()?.customerId)
        )
    }

    override suspend fun cancelSubscription(subscriptionId: String): Resource<MessageResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.cancelSubscription(getToken(), subscriptionId)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun setupPaymentIntent(
        paymentMethods: List<String>,
        customerId: String
    ): Resource<SetupPaymentIntentResponse> {
        return goodJobApiImpl.setupPaymentIntent(getToken(), paymentMethods, customerId)
    }

    override suspend fun subscribeToPlan(
        planId: String,
        customerId: String
    ): Resource<SubscribeToPlanResponse> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.subscribeToPlan(getToken(), planId, customerId)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("subscribeToPlan") { "subscribeToPlan SUCCESS ${data.user?.planId}" }
                        data.user?.let { saveUser(it) }
                        Resource.Success(data)
                    } else {
                        log.d("subscribeToPlan") { "subscribeToPlan ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "subscribeToPlan An error occurred" }
                    Resource.Error(apiResponse.text ?: stringResourcePlain(Res.string.unknown_error))
                }
            }
        } catch (e: Exception) {
            log.e { "subscribeToPlan Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun checkRestrictions() {
        getUser()?.planId?.let { planId ->
            val plan = getPlan(planId)
            val resource = getUsers()
            if (resource is Resource.Success) {
                val users = resource.data.orEmpty()

                val usersMap = users
                    .filter { it.status == UserStatus.active.name }
                    .groupBy { it.role }

                val managerCount = usersMap[Role.manager.name]?.size ?: 0
                val employeeCount = usersMap[Role.employee.name]?.size ?: 0

                setManagerNumberExceeded(
                    managerCount > (plan?.limitManagers?.toInt() ?: Int.MAX_VALUE)
                )
                setEmployeeNumberExceeded(
                    employeeCount > (plan?.limitUsers?.toInt() ?: Int.MAX_VALUE)
                )
            } else {
                setManagerNumberExceeded(false)
                setEmployeeNumberExceeded(false)
            }
        }
    }


    //***************** FILES *********************************

    override suspend fun getFile(fileId: String): Resource<FileResult> {
        return try {
            when (val apiResponse = goodJobApiImpl.getFile(fileId, getToken())) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("getFile") { "getFile SUCCESS ${data.size}" }
                        val fileType = FileUtil().getFileType(fileId)
                        fileManager.saveFile(fileId, data)
                        Resource.Success(FileResult(data, fileType))
                    } else {
                        log.e { "getFile ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "getFile ERROR An error occurred" }
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "getFile Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override fun getImage(imageFileId: String): BitmapPainter? {
        return fileManager.getBitmap(imageFileId)
    }

    override fun getImageFromByteArray(byteArray: ByteArray): BitmapPainter? {
        return fileManager.getBitmapFromByteArray(byteArray)
    }

    override fun getSavedFilePath(fileId: String): String? {
        return fileManager.getFilePath(fileId)
    }

    override suspend fun uploadFile(
        fileData: ByteArray,
        fileId: String
    ): Resource<UploadFileResponse> {
        return goodJobApiImpl.uploadFile(getToken(), fileData, fileId)
    }

    override suspend fun deleteFileFromJob(jobId: String, fileId: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteFile(getToken(), fileId)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        val existingJob = getJobByIdFlow(jobId).firstOrNull()
                        if (existingJob != null) {
                            // Deserialize the  list
                            val files = existingJob.fileObjects

                            // Remove the specified fileId
                            val updatedFiles =
                                existingJob.fileObjects?.filterNot { it?.documentFileId == fileId }
                            // Update the job object with the new files list
                            val updatedJob = existingJob.copy(
                                fileObjects = updatedFiles
                            )

                            // Make an API call to update the job
                            when (val updateJobResponse =
                                updatedJob.let { Mapper().toJobUpdateRequest(it) }.let {
                                    goodJobApiImpl.updateJob(
                                        getToken(), jobId, it
                                    )
                                }) {
                                is Resource.Success -> {
                                    fileManager.deleteFile(fileId)
                                    Resource.Success(stringResourcePlain(Res.string.file_deleted))
                                }

                                is Resource.Error -> {
                                    Resource.Error(updateJobResponse.text)
                                }
                            }
                        }
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteFile(fileId: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteFile(getToken(), fileId)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        // TODO !!!!!   fileManager.deleteFile(fileId)
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun subscribeUser(email: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.subscribeUser(email)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    //LOCAL STORAGE
    // TODO for token we should save it in cookie for web, it is safer
    override fun saveToken(token: String) {
        log.d("saveToken") { "saveToken" }
        prefs.saveToken(token)
    }

    override fun getToken() = prefs.getToken()

    override fun saveRefreshToken(refreshToken: String) {
        prefs.saveRefreshToken(refreshToken)
    }

    override fun getRefreshToken(): String {
        return prefs.getRefreshToken()
    }

    override fun saveEmail(email: String) {
        prefs.saveEmail(email)
    }

    override fun getEmail(): String {
        return prefs.getEmail()
    }

    override fun saveWorkspaceId(workspaceId: String) {
        prefs.saveWorkspaceId(workspaceId)
    }

    override fun getWorkspaceId(): String {
        return prefs.getWorkspaceId()
    }

    override fun setEmployeeNumberExceeded(isEmployeeNumberExceeded: Boolean) {
        prefs.setEmployeeNumberExceeded(isEmployeeNumberExceeded)
    }

    override fun isEmployeeNumberExceeded(): Boolean {
        return prefs.employeeNumberExceeded.value
    }

    override fun setManagerNumberExceeded(isManagerNumberExceeded: Boolean) {
        prefs.setManagerNumberExceeded(isManagerNumberExceeded)
    }

    override fun isManagerNumberExceeded(): Boolean {
        return prefs.managerNumberExceeded.value
    }

    override fun getFCMPushToken(): String {
        return ""
    }

    override fun updateFCMPushToken(token: String) {

    }

    override fun getWidQuery(): String {
        return prefs.getWidQuery()
    }

    override fun setWidQuery(wid: String) {
        prefs.setWidQuery(wid)
    }
}